import { IntercomHelper } from "@src/layouts/NavigationBar/IntercomHelper";
import { queryClient } from "@src/index";
import { useEffect, useState } from "react";
import { QueryClientProvider } from "react-query";
import { BrowserRouter } from "react-router";
import { IntercomProvider } from "react-use-intercom";
import { Popup } from "../Popup/Popup";
import styles from "./PopupRoot.module.css";

export const PopupRootContainer = () => {
  return (
    <IntercomProvider appId={IntercomHelper.INTERCOM_ID}>
      <PopupRoot />
    </IntercomProvider>
  );
};

export const PopupRoot = () => {
  const [popups, setPopups] = useState<JSX.Element[]>([]);

  const popup_div = document.getElementById(Popup.popup_div_id);

  function wrapInParentPopup(id: string, child: JSX.Element) {
    return <PopupParent key={id} id={id} zIndex={1000 + popups.length} child={child} />;
  }

  /**
   * Setting the present/remove-handlers of the static Popup class.
   * The functions need to be refreshed every time 'popups' changes
   * in order to refer to the correct 'popups'.
   */
  useEffect(() => {
    Popup.presentHandler = (popup: JSX.Element, backshadow?: boolean, duration: number = 0, animated?: boolean) => {
      /**
       * Have to wait in order to enable callbacks for proper removals.
       * This is because if you try to add and remove a popup at the same time
       * there is confusion around which 'popups'-version. This removes all
       * popups some times when popups are stacked (not entirely sure why).
       */
      setTimeout(() => {
        setPopups((popups) => [...popups, wrapInParentPopup(popup.props.id, popup)]);

        if (duration) {
          setTimeout(() => {
            Popup.remove(popup.props.id, false);
          }, duration);
        }
      }, 1);
    };

    /* User reported problem with popup not displaying when using external mouse.
      Identified that the problem was due to the popup being removed instantly after open.
      Therefor a small timeout on the remove handler
    */
    setTimeout(() => {
      Popup.removeHandler = (id: string, animated: boolean = false, duration: number = 0) => {
        setPopups((popups) => popups.filter((elem) => elem.props.id !== id));
      };
    }, 0.05);
  }, []);

  useEffect(() => {
    if (popups.length < 1 && popup_div) {
      popup_div.style.display! = "none";
    } else if (popup_div) {
      popup_div.style.display = "block";
    }
    const newPopups: JSX.Element[] = [];
    for (const popup of popups) {
      if (!newPopups.some((old) => old.props.id === popup.props.id)) {
        newPopups.push(popup);
      }
    }
    if (newPopups.length !== popups.length) setPopups(newPopups);
  }, [popups]);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>{popups}</BrowserRouter>
    </QueryClientProvider>
  );
};

interface PopupParentProps {
  id: string;
  child: JSX.Element;
  zIndex: number;
}

const PopupParent = (props: PopupParentProps) => {
  return (
    <QueryClientProvider client={queryClient}>
      <div className={styles.popupParent} style={{ zIndex: props.zIndex }}>
        {props.child}
      </div>
    </QueryClientProvider>
  );
};
